<template>
  <header
    class="bg-white dark:bg-neutral-950 shadow-sm fixed w-full top-0 z-[6] transition-transform"
    :class="{
      '-translate-y-[var(--header-height)]': !headerVisible,
      'translate-y-0': headerVisible,
    }"
  >
    <ContainerBasic
      :class="{
        'max-w-full': expanded,
      }"
    >
      <nav class="flex items-center justify-between py-2" aria-label="Global">
        <HeaderLogo />
        <PopoverGroup class="hidden lg:flex lg:gap-x-12">
          <UDropdown :items="portalPages" :popper="{ placement: 'bottom-start' }">
            <UButton :class="'!bg-transparent p-0'" />
            <span class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200">
              <template v-if="!hasAccess">
                <i class="fas fa-lock text-cardinal mr-1"></i>
              </template>
              Portal
              <i class="far fa-chevron-down"></i>
            </span>
          </UDropdown>
          <UDropdown :items="statsPages" :popper="{ placement: 'bottom-start' }">
            <UButton :class="'!bg-transparent p-0'" />
            <span class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200">
              <template v-if="!hasAccess">
                <i class="fas fa-lock text-cardinal mr-1"></i>
              </template>
              Stats
              <i class="far fa-chevron-down"></i>
            </span>
          </UDropdown>
          <UDropdown :items="rankingPages" :popper="{ placement: 'bottom-start' }">
            <UButton :class="'!bg-transparent p-0'" />
            <span class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200">
              <template v-if="!hasAccess">
                <i class="fas fa-lock text-cardinal mr-1"></i>
              </template>
              Rankings
              <i class="far fa-chevron-down"></i>
            </span>
          </UDropdown>
          <NuxtLink
            to="/players"
            class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200"
            >Players</NuxtLink
          >

          <NuxtLink
            to="/teams"
            class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200"
            >Teams</NuxtLink
          >
          <NuxtLink
            to="/pricing"
            class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200"
          >
            Pricing
          </NuxtLink>
          <NuxtLink
            to="/articles"
            class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200"
          >
            Articles
          </NuxtLink>
        </PopoverGroup>
        <div v-if="user" class="flex items-center gap-3">
          <div class="lg:flex">
            <HeaderAuthDropdown />
          </div>
          <UTooltip
            :text="`${$colorMode.preference == 'dark' ? 'Dark' : 'Light'} Mode`"
            :popper="{ arrow: true }"
          >
            <i
              class="fas fa-sun cursor-pointer inline-flex dark:hidden"
              @click="onChangeColorMode('dark')"
            />
            <i
              class="fas fa-moon cursor-pointer hidden dark:inline-flex"
              @click="onChangeColorMode('light')"
            />
          </UTooltip>
          <button
            type="button"
            class="-m-2.5 inline-flex lg:hidden items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-200"
            @click="mobileMenuOpen = true"
          >
            <span class="sr-only">Open main menu</span>
            <i class="fas fa-bars text-2xl"></i>
          </button>
        </div>
        <div v-else class="flex items-center gap-5">
          <div class="hidden lg:flex">
            <NuxtLink
              to="/login"
              class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-200 flex items-center gap-2"
            >
              Sign In
            </NuxtLink>
          </div>
          <UButton
            v-if="!user"
            to="/sign-up"
            class="bg-cardinal-700 dark:!bg-cardinal-700 dark:!text-white font-bold px-4 py-2 lg:px-5 py-3"
          >
            Sign Up
          </UButton>
          <UTooltip
            :text="`${$colorMode.preference == 'dark' ? 'Dark' : 'Light'} Mode`"
            :popper="{ arrow: true }"
          >
            <i
              class="fas fa-sun cursor-pointer inline-flex dark:hidden"
              @click="onChangeColorMode('dark')"
            />
            <i
              class="fas fa-moon cursor-pointer hidden dark:inline-flex"
              @click="onChangeColorMode('light')"
            />
          </UTooltip>
          <div class="flex lg:hidden">
            <button
              type="button"
              class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-200"
              @click="mobileMenuOpen = true"
            >
              <span class="sr-only">Open main menu</span>
              <i class="fas fa-bars text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>
    </ContainerBasic>
    <Dialog as="div" class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
      <div class="fixed inset-0 z-10" />
      <DialogPanel
        class="fixed inset-y-0 right-0 z-10 w-full px-4 pt-2 pb-6 overflow-y-auto bg-white dark:bg-neutral-950 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
      >
        <div class="flex items-center justify-between">
          <HeaderLogo />
          <button
            type="button"
            class="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-white"
            @click="mobileMenuOpen = false"
          >
            <span class="sr-only">Close menu</span>
            <i class="fas fa-xmark text-2xl"></i>
          </button>
        </div>
        <div class="flow-root mt-6">
          <div class="-my-6 divide-y divide-gray-500/10 dark:divide-neutral-700">
            <div class="py-6 space-y-2">
              <Disclosure as="div" class="-mx-3" v-slot="{ open }">
                <DisclosureButton
                  class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                >
                  <template v-if="!hasAccess">
                    <i class="fas fa-lock text-cardinal mr-2"></i>
                  </template>
                  Portal
                  <i
                    class="fas fa-chevron-down ml-auto"
                    :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
                    aria-hidden="true"
                  />
                </DisclosureButton>
                <DisclosurePanel class="mt-2 space-y-2">
                  <div v-for="section in [...portalPages]">
                    <DisclosureButton
                      v-for="(item, index) in section"
                      :key="index"
                      as="a"
                      @click="item.click"
                      class="block py-2 pl-6 pr-3 text-sm leading-7 text-gray-900 dark:text-neutral-400 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
                    >
                      {{ item.label }}
                    </DisclosureButton>
                  </div>
                </DisclosurePanel>
              </Disclosure>
              <Disclosure as="div" class="-mx-3" v-slot="{ open }">
                <DisclosureButton
                  class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                >
                  <template v-if="!hasAccess">
                    <i class="fas fa-lock text-cardinal mr-2"></i>
                  </template>
                  Stats
                  <i
                    class="fas fa-chevron-down ml-auto"
                    :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
                    aria-hidden="true"
                  />
                </DisclosureButton>
                <DisclosurePanel class="mt-2 space-y-2">
                  <div v-for="section in [...statsPages]">
                    <DisclosureButton
                      v-for="(item, index) in section"
                      :key="index"
                      as="a"
                      @click="item.click"
                      class="block py-2 pl-6 pr-3 text-sm leading-7 text-gray-900 dark:text-neutral-400 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
                      >{{ item.label }}</DisclosureButton
                    >
                  </div>
                </DisclosurePanel>
              </Disclosure>
              <Disclosure as="div" class="-mx-3" v-slot="{ open }">
                <DisclosureButton
                  class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                >
                  <template v-if="!hasAccess">
                    <i class="fas fa-lock text-cardinal mr-2"></i>
                  </template>
                  Rankings
                  <i
                    class="fas fa-chevron-down ml-auto"
                    :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
                    aria-hidden="true"
                  />
                </DisclosureButton>
                <DisclosurePanel class="mt-2 space-y-2">
                  <div v-for="section in [...rankingPages]">
                    <DisclosureButton
                      v-for="(item, index) in section"
                      :key="index"
                      as="a"
                      @click="item.click"
                      class="block py-2 pl-6 pr-3 text-sm leading-7 text-gray-900 dark:text-neutral-400 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
                      >{{ item.label }}</DisclosureButton
                    >
                  </div>
                </DisclosurePanel>
              </Disclosure>
              <NuxtLink
                to="/players"
                class="block px-3 py-2 -mx-3 text-base font-semibold leading-7 text-gray-900 dark:text-white rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                Players
              </NuxtLink>
              <NuxtLink
                to="/teams"
                class="block px-3 py-2 -mx-3 text-base font-semibold leading-7 text-gray-900 dark:text-white rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                Teams
              </NuxtLink>

              <NuxtLink
                to="/pricing"
                class="block px-3 py-2 -mx-3 text-base font-semibold leading-7 text-gray-900 dark:text-white rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                Pricing
              </NuxtLink>
              <NuxtLink
                to="/articles"
                class="block px-3 py-2 -mx-3 text-base font-semibold leading-7 text-gray-900 dark:text-white rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                Articles
              </NuxtLink>
            </div>
            <div v-if="!user" class="py-6">
              <NuxtLink
                to="/login"
                class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-900"
                >Log in</NuxtLink
              >
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>

<script lang="ts" setup>
const user = useSupabaseUser();
const store = useProfileStore();
const { profile, hasAccess } = storeToRefs(store);
const { headerVisible } = useHeadroom();
const colorMode = useColorMode();

type Props = {
  expanded?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  expanded: false,
});

const items = [
  [
    {
      label: 'Team',
      click: () => {
        navigateTo('/rankings/team');
      },
    },
  ],
];

import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/vue';

const portalPages = [
  [
    {
      label: 'The Board',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/the-board');
      },
    },
    {
      label: 'Teams',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/portal/teams');
      },
    },
    {
      label: 'Players',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/portal/players');
      },
    },
  ],
];
const rankingPages = [
  [
    {
      label: 'Teams',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/teams');
      },
    },
    {
      label: 'Team Hitting',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/teams/hitting');
      },
    },
    {
      label: 'Team Pitching',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/teams/pitching');
      },
    },
  ],
  [
    {
      label: 'Player Hitting',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/player/hitting');
      },
    },
    {
      label: 'Player Pitching',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/rankings/player/pitching');
      },
    },
  ],
];
const statsPages = [
  [
    {
      label: 'Team Hitting',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/stats/teams/hitting');
      },
    },
    {
      label: 'Team Pitching',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/stats/teams/pitching');
      },
    },
  ],
  [
    {
      label: 'Player Hitting',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/stats/players/hitting');
      },
    },
    {
      label: 'Player Pitching',
      // description: 'Get a better understanding of your traffic',
      click: () => {
        mobileMenuOpen.value = false;
        navigateTo('/stats/players/pitching');
      },
    },
  ],
];
const mobileMenuOpen = ref(false);
const route = useRoute();
watch(
  () => route.name,
  () => {
    // This function will be called whenever the route changes
    mobileMenuOpen.value = false; // Set the ref to false when the route changes
  }
);

const onChangeColorMode = (color: 'light' | 'dark') => {
  console.log('ColorMode', color);
  colorMode.preference = color;
};
</script>
